import { Injectable, inject, isDevMode, signal, computed } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { LoggedUserModel, PrimaryRole } from './logged-user.model';
import { RETURN_URL_KEY } from './redirect-to-sign-in';
import { SiteStore } from '@core/site/site.store';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private oidc = inject(OidcSecurityService);
  private router = inject(Router);
  private site = inject(SiteStore);

  private _user = signal<LoggedUserModel | null>(null);
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);
  readonly isSuperAdmin = computed(() => this._user()?.primaryRole === PrimaryRole.SuperAdmin);

  /**
   * Awaited from an app initializer so the initial route's guards (e.g.
   * maintenanceModeGuard) see resolved auth/role state instead of racing it —
   * angular-auth-oidc-client updates userData$ as part of this same checkAuth flow.
   *
   * Never rejects: Angular's app initializers reject the whole bootstrap if any of
   * them throw, so an OIDC hiccup here (auth server down, discovery doc fetch
   * failing) would otherwise break the app for every visitor, not just gate this
   * check. Fails closed instead — same fail-safe shape as SiteStore.load().
   */
  async checkAuth(): Promise<void> {
    // Keep the user signal in sync with all token changes (initial load + silent refreshes)
    this.oidc.userData$.subscribe(({ userData }) => {
      if (userData) {
        this._user.set({
          id: Number(userData['sub']),
          name: userData['given_name'] ?? '',
          displayName: userData['display_name'] ?? '',
          initial: (userData['given_name'] as string)?.slice(0, 1) ?? '',
          email: userData['email'] ?? '',
          primaryRole:
            userData['primary_role'] !== undefined ? Number(userData['primary_role']) : null,
        });
      }
    });

    try {
      // One-time check: handle post-login return URL redirect
      const { isAuthenticated } = await firstValueFrom(this.oidc.checkAuth());
      if (isAuthenticated) {
        const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
        if (returnUrl) {
          sessionStorage.removeItem(RETURN_URL_KEY);
          this.router.navigateByUrl(returnUrl);
        }
      }
    } catch (err) {
      console.error('OIDC checkAuth failed', err);
      // Fall through: app still boots, user is treated as unauthenticated/non-SuperAdmin.
    }
  }

  getAccessToken() {
    return this.oidc.getAccessToken();
  }

  /**
   * No-op when the tenant has no account module (#585). The check belongs here, not only on the
   * route: `oidc.authorize()` is a full-page browser redirect, so a guard never sees it — see
   * `.claude/specs/abook-customer-app/core/tenant.md` § Feature-module guards.
   */
  signIn(returnUrl?: string, extraQueryParams?: Record<string, string>): void {
    if (!this.site.showAccountModule()) {
      // The basket's LoginRequiredDialog can still reach this until #586 turns its module off,
      // and a button that silently does nothing is hard to diagnose from a bug report.
      if (isDevMode()) {
        console.warn('signIn() ignored: this tenant has showAccountModule: false');
      }
      return;
    }
    if (returnUrl) {
      sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    }
    this.oidc.authorize(undefined, { customParams: extraQueryParams });
  }

  refreshSession(): Observable<void> {
    return this.oidc.forceRefreshSession().pipe(map(() => void 0));
  }

  signOut(): void {
    this._user.set(null);
    this.oidc.logoff().subscribe();
  }

  /**
   * Drops this origin's token state without calling the auth server's end-session endpoint —
   * for the front-channel logout iframe, where the server has already ended the session.
   */
  signOutLocal(): void {
    this._user.set(null);
    this.oidc.logoffLocal();
  }

  /**
   * The auth server's session id (`sid` claim of the id_token), or `null` when there is no
   * token or it has none — for the front-channel logout page, which only acts on the session
   * named in its URL.
   */
  sessionId(): Observable<string | null> {
    return this.oidc
      .getPayloadFromIdToken()
      .pipe(map((payload) => (typeof payload?.sid === 'string' ? payload.sid : null)));
  }
}
